import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import { required } from 'components/helpers/validators';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessorEquipmentOptions } from '../hooks';
import { AccessorFormModel, AccessorFormValues } from '../types';
import { normalizeEquipmentId } from '../utils';
import SectionHeader from 'components/ui/typography/SectionHeader';

interface AccessoryFormProps {
  accessor?: AccessorFormModel;
  onSubmit: (values: AccessorFormValues) => Promise<void> | void;
  isDisabledEdit?: boolean;
}

function AccessoryForm({ accessor, onSubmit, isDisabledEdit }: AccessoryFormProps) {
  const { t } = useTranslation();
  const { equipments } = useAccessorEquipmentOptions();
  const translationPrefix = 'entities.accessor';

  const isEdit = Boolean(
    accessor && typeof accessor === 'object' && 'id' in accessor && accessor.id != null
  );

  const equipmentId = normalizeEquipmentId(accessor?.equipment);
  const equipmentInit = useMemo(
    () => (equipmentId != null ? equipments.find((eq) => eq.id === equipmentId) : undefined),
    [equipments, equipmentId]
  );

  const formInitialValues =
    isEdit && accessor
      ? {
          code: accessor.code,
          description: accessor.description,
          serial_number: accessor.serial_number,
          equipment: equipmentId ?? '',
          internal_id: accessor.internal_id ?? '',
          transport_document_ref: accessor.transport_document_ref ?? '',
          delivery_date: accessor.delivery_date ?? null,
          installation_date: accessor.installation_date ?? null,
          testing_date: accessor.testing_date ?? null,
          buying_date: accessor.buying_date ?? null,
          decommission_date: accessor.decommission_date ?? null
        }
      : undefined;

  if (!accessor) return null;

  return (
    <EnhancedForm
      key={`accessor-form-${'id' in accessor && accessor.id != null ? accessor.id : 'new'}-${
        equipments.length
      }`}
      onSubmit={onSubmit}
      edit={isEdit}
      initialValues={formInitialValues}
      autoStyleLayout={true}
      columns={4}>
      <IdentityField
        gridSpan={2}
        name="code"
        initialValue={accessor.code}
        component={TextFieldAdapter}
        label={t('global.code')}
        type="text"
        validate={required}
        disabled={isDisabledEdit}
        required
      />
      <IdentityField
        gridSpan={2}
        name="description"
        initialValue={accessor.description}
        component={TextFieldAdapter}
        label={t('global.description')}
        type="text"
        validate={required}
        disabled={isDisabledEdit}
        required
      />
      <IdentityField
        gridSpan={2}
        name="serial_number"
        initialValue={accessor.serial_number}
        component={TextFieldAdapter}
        label={t('global.serial_number')}
        type="text"
        validate={required}
        disabled={isDisabledEdit}
        required
      />
      <IdentityField
        gridSpan={2}
        name="equipment"
        component={AutocompleteAdapter}
        options={equipments}
        initValue={equipmentInit}
        optionLabel="code"
        placeholder={t('global.equipment')}
        type="text"
        disabled={isDisabledEdit}
        required
        autoSelect={false}
      />
      <SectionHeader title={t('pages.accessories.administrative-data')} gridSpan />
      <IdentityField
        gridSpan={2}
        name="internal_id"
        initialValue={accessor?.internal_id ?? ''}
        component={TextFieldAdapter}
        label={t(`${translationPrefix}.internal_id`)}
        type="text"
        disabled={isDisabledEdit}
      />
      <IdentityField
        gridSpan={2}
        name="transport_document_ref"
        initialValue={accessor?.transport_document_ref ?? ''}
        component={TextFieldAdapter}
        label={t(`${translationPrefix}.transport_document_ref`)}
        type="text"
        disabled={isDisabledEdit}
      />
      <IdentityField
        name="delivery_date"
        initialValue={accessor?.delivery_date ?? null}
        component={DatePickerAdapter}
        label={t(`${translationPrefix}.delivery_date`)}
        type="date"
        disabled={isDisabledEdit}
      />
      <IdentityField
        name="installation_date"
        initialValue={accessor?.installation_date ?? null}
        component={DatePickerAdapter}
        label={t(`${translationPrefix}.installation_date`)}
        type="date"
        disabled={isDisabledEdit}
      />
      <IdentityField
        name="testing_date"
        initialValue={accessor?.testing_date ?? null}
        component={DatePickerAdapter}
        label={t(`${translationPrefix}.testing_date`)}
        type="date"
        disabled={isDisabledEdit}
      />
      <IdentityField
        name="buying_date"
        initialValue={accessor?.buying_date ?? null}
        component={DatePickerAdapter}
        label={t(`${translationPrefix}.buying_date`)}
        type="date"
        disabled={isDisabledEdit}
      />
      <IdentityField
        name="decommission_date"
        initialValue={accessor?.decommission_date ?? null}
        component={DatePickerAdapter}
        label={t(`${translationPrefix}.decommission_date`)}
        type="date"
        disabled={isDisabledEdit}
      />
    </EnhancedForm>
  );
}

export default AccessoryForm;
